import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'

/** Number transfers into Callizy (backend app `portage`). */

export type PortStatus =
  | 'brouillon'
  | 'recue'
  | 'a_corriger'
  | 'verifiee'
  | 'envoyee'
  | 'date_confirmee'
  | 'transferee'
  | 'annulee'

export type PortLineType = 'fixe' | 'cellulaire' | 'sans_frais'
export type PortTimeOfDay = 'matin' | 'apres_midi'
export type PortTimeZone =
  | 'est' | 'pacifique' | 'rocheuses' | 'saskatchewan' | 'centre' | 'atlantique' | 'terre_neuve' | 'alaska' | 'hawai'
export type PortScope = 'tous' | 'partie'
export type PortSignatureState = 'absente' | 'valide' | 'champs_changes' | 'expiree'
export type PortStepKey = 'recue' | 'verifiee' | 'envoyee' | 'date_confirmee' | 'transferee'

export const PORT_STEPS: PortStepKey[] = ['recue', 'verifiee', 'envoyee', 'date_confirmee', 'transferee']

export interface PortNumber {
  id: number
  phone_number: string
  line_type: PortLineType
  carrier: string
  /** The account number and PIN never come back from the server: only whether they are set. */
  account_number_set: boolean
  pin_set: boolean
  /** `arrive`: in the account, not set up yet (the team does it). Shown to the client as still waiting. */
  status: 'attendu' | 'arrive' | 'branche' | 'transfere' | 'rejete'
  rejection_reason: string
  phone_number_id: number | null
}

export interface PortDocument {
  id: number
  type: 'facture' | 'lettre_signee' | 'lettre_manuscrite'
  name: string
  size: number
  content_type: string
  created_at: string
}

export interface PortEvent {
  type: string
  at: string
  phone_number: string
  reason: string
}

export interface PortRequest {
  id: string
  status: PortStatus
  origin: string
  first_name: string
  last_name: string
  business_name: string
  address: string
  address2: string
  city: string
  province: string
  postal_code: string
  scope: PortScope
  signer_name: string
  signer_title: string
  signer_email: string
  confirmed_date: string | null
  confirmed_time: PortTimeOfDay | ''
  /** HH:MM in the company's time zone, when the operator gave the exact time. */
  confirmed_hour: string | null
  /** That time zone, by family: est, pacifique, rocheuses... */
  confirmed_zone: PortTimeZone | null
  temporary_number: { id: number; phone_number: string } | null
  numbers: PortNumber[]
  carriers: string[]
  correction_message: string
  can_edit: boolean
  can_sign: boolean
  can_cancel: boolean
  cancel_deadline: string | null
  signature: { mode: 'electronique' | 'manuscrite'; signed_at: string; signer_name: string } | null
  signature_state: PortSignatureState
  /** Field names (API names) still missing before signing, e.g. `numbers.0.pin`, `invoice`. */
  missing: string[]
  invoice: PortDocument | null
  letter: PortDocument | null
  steps: Record<PortStepKey, string | null>
  created_at: string
  submitted_at: string | null
  cancelled_at: string | null
  events?: PortEvent[]
}

export interface PortOptions {
  provinces: string[]
  temporary_numbers: Array<{ id: number; phone_number: string; label: string; is_main: boolean }>
  default_temporary_number_id: number | null
}

export interface PortNumberInput {
  phone_number: string
  line_type: PortLineType
  carrier: string
  /** Omitted: the server keeps the stored value. */
  account_number?: string
  pin?: string
}

export interface PortRequestInput {
  first_name?: string
  last_name?: string
  business_name?: string
  address?: string
  address2?: string
  city?: string
  province?: string
  postal_code?: string
  scope?: PortScope
  signer_name?: string
  signer_title?: string
  signer_email?: string
  temporary_number_id?: number | null
  numbers?: PortNumberInput[]
}

export interface PortChecks {
  owner: boolean
  matches_bill: boolean
  authorize: boolean
  electronic?: boolean
}

/** Server refusal: `error` is a code, `fields` the API field names concerned. */
export interface PortApiError {
  error?: string
  fields?: string[]
}

export function portErrorOf(err: unknown): PortApiError & { status?: number } {
  const e = err as { response?: { status?: number; data?: PortApiError } }
  return { ...(e.response?.data ?? {}), status: e.response?.status }
}

const listKey = (companyId: number | undefined) => ['port-requests', companyId]
const detailKey = (companyId: number | undefined, id: string | null | undefined) => ['port-request', companyId, id]

export function usePortRequests(companyId: number | undefined) {
  return useQuery({
    queryKey: listKey(companyId),
    queryFn: async () => {
      const { data } = await api.get<{ transfers: PortRequest[]; options: PortOptions }>(
        `/api/companies/${companyId}/portage/`,
      )
      return data
    },
    enabled: !!companyId,
    staleTime: 30_000,
  })
}

export function usePortRequest(companyId: number | undefined, id: string | null | undefined) {
  return useQuery({
    queryKey: detailKey(companyId, id),
    queryFn: async () => {
      const { data } = await api.get<PortRequest>(`/api/companies/${companyId}/portage/${id}/`)
      return data
    },
    enabled: !!companyId && !!id,
  })
}

function useStore(companyId: number | undefined) {
  const queryClient = useQueryClient()
  return (data: PortRequest) => {
    queryClient.setQueryData(detailKey(companyId, data.id), data)
    queryClient.invalidateQueries({ queryKey: listKey(companyId) })
    // A finished transfer adds a line to the numbers list.
    queryClient.invalidateQueries({ queryKey: ['company-phone-numbers', companyId] })
  }
}

export function useSavePortRequest(companyId: number | undefined) {
  const store = useStore(companyId)
  return useMutation({
    mutationFn: async ({ id, input }: { id?: string | null; input: PortRequestInput }) => {
      const { data } = id
        ? await api.patch<PortRequest>(`/api/companies/${companyId}/portage/${id}/`, input)
        : await api.post<PortRequest>(`/api/companies/${companyId}/portage/`, input)
      return data
    },
    onSuccess: store,
  })
}

export function useUploadPortInvoice(companyId: number | undefined) {
  const store = useStore(companyId)
  return useMutation({
    mutationFn: async ({ id, file }: { id: string; file: File }) => {
      const form = new FormData()
      form.append('file', file)
      const { data } = await api.post<PortRequest>(`/api/companies/${companyId}/portage/${id}/invoice/`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120_000,
      })
      return data
    },
    onSuccess: store,
  })
}

export function useSignPortRequest(companyId: number | undefined) {
  const store = useStore(companyId)
  return useMutation({
    mutationFn: async ({
      id,
      checks,
      signaturePng,
      signaturePoints,
    }: {
      id: string
      checks: PortChecks
      signaturePng: string
      signaturePoints: number[][][]
    }) => {
      const { data } = await api.post<PortRequest>(
        `/api/companies/${companyId}/portage/${id}/sign/`,
        { checks, signature_png: signaturePng, signature_points: signaturePoints },
        { timeout: 60_000 },
      )
      return data
    },
    onSuccess: store,
  })
}

export function useUploadSignedLetter(companyId: number | undefined) {
  const store = useStore(companyId)
  return useMutation({
    mutationFn: async ({ id, file, checks }: { id: string; file: File; checks: PortChecks }) => {
      const form = new FormData()
      form.append('file', file)
      form.append('checks', JSON.stringify(checks))
      const { data } = await api.post<PortRequest>(
        `/api/companies/${companyId}/portage/${id}/signed-letter/`,
        form,
        { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120_000 },
      )
      return data
    },
    onSuccess: store,
  })
}

export function useCancelPortRequest(companyId: number | undefined) {
  const store = useStore(companyId)
  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason?: string }) => {
      const { data } = await api.post<PortRequest>(`/api/companies/${companyId}/portage/${id}/cancel/`, {
        reason: reason ?? '',
      })
      return data
    },
    onSuccess: store,
  })
}

/** Downloads the filled-in letter, unsigned, to sign by hand. */
export async function downloadPortLetter(companyId: number, id: string) {
  const { data } = await api.get<Blob>(`/api/companies/${companyId}/portage/${id}/letter/`, {
    responseType: 'blob',
  })
  const url = URL.createObjectURL(data)
  const a = document.createElement('a')
  a.href = url
  a.download = 'lettre-autorisation-transfert.pdf'
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 10_000)
}

/** Opens a document (invoice, signed letter) through a short-lived link. */
export async function openPortDocument(companyId: number, id: string, documentId: number) {
  // Opened before the request: Safari blocks a window opened after an await.
  const tab = window.open('about:blank', '_blank')
  try {
    const { data } = await api.get<{ url: string }>(
      `/api/companies/${companyId}/portage/${id}/documents/${documentId}/`,
    )
    if (tab) tab.location.href = data.url
    else window.location.href = data.url
  } catch (err) {
    tab?.close()
    throw err
  }
}

// =============================================================================
// Signing on a phone, with a QR code shown on the computer
// =============================================================================

/** valide: waiting for the phone. Every other state means the code no longer works. */
export type PhoneSignState = 'valide' | 'signe' | 'remplace' | 'expire' | 'trop_essais' | 'change' | 'aucun'

export interface PhoneSignCode {
  id: number
  state: PhoneSignState
  expires_at: string
  /** Only in the answer that creates it: the server keeps a hash. */
  code?: string
  /** Once signed: the transfer as it is now. */
  transfer?: PortRequest
}

export function useCreatePhoneSignCode(companyId: number | undefined) {
  return useMutation({
    mutationFn: async ({ id, checks }: { id: string; checks: PortChecks }) => {
      const { data } = await api.post<PhoneSignCode>(`/api/companies/${companyId}/portage/${id}/phone-sign/`, {
        checks,
      })
      return data
    },
  })
}

/** Asked every 3 seconds while the code is on screen: the computer learns when the phone has signed. */
export function usePhoneSignStatus(companyId: number | undefined, id: string, codeId: number, enabled: boolean) {
  return useQuery({
    queryKey: ['port-phone-sign', companyId, id, codeId],
    queryFn: async () => {
      const { data } = await api.get<PhoneSignCode>(`/api/companies/${companyId}/portage/${id}/phone-sign/`)
      return data
    },
    enabled: enabled && !!companyId && !!id,
    refetchInterval: enabled ? 3000 : false,
    refetchIntervalInBackground: true,
    gcTime: 0,
  })
}

/** Keeps the transfer in the cache once the phone has signed. */
export function useStorePortRequest(companyId: number | undefined) {
  return useStore(companyId)
}

/** What the phone shows before signing. Public: the code alone opens it, without logging in. */
export interface PublicSignRequest {
  state: 'valide'
  expires_at: string
  first_name: string
  last_name: string
  business_name: string
  signer_name: string
  numbers: string[]
}

export class PhoneSignError extends Error {
  code: string
  status: number

  constructor(code: string, status: number) {
    super(code)
    this.code = code
    this.status = status
  }
}

const publicSignUrl = (code: string) =>
  `${api.defaults.baseURL}/api/portage/signature/${encodeURIComponent(code.trim().toUpperCase())}/`

async function readPublic<T>(res: Response): Promise<T> {
  const data = await res.json().catch(() => ({}))
  if (!res.ok) throw new PhoneSignError((data as PortApiError).error ?? 'erreur', res.status)
  return data as T
}

export async function fetchPublicSignRequest(code: string): Promise<PublicSignRequest> {
  const res = await fetch(publicSignUrl(code), { headers: { Accept: 'application/json' } })
  return readPublic<PublicSignRequest>(res)
}

export async function submitPublicSignature(code: string, png: string, points: number[][][]): Promise<void> {
  const res = await fetch(publicSignUrl(code), {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify({ signature_png: png, signature_points: points }),
  })
  await readPublic<{ state: string }>(res)
}
